import { useState } from 'react';

import { Helmet } from 'react-helmet-async';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Seo from '@/components/Seo/Seo';
import { color } from '@/themes/color';
import { typography } from '@/themes/typography';
import backgroundImage from '@/assets/faq.svg';
import {
  type FAQCategory,
  FAQ_ITEMS,
  CATEGORIES,
  FAQ_SCHEMA,
  FAQ_URL,
} from '@/constants/faqData';

export default function FaqPage(): JSX.Element {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] =
    useState<FAQCategory>('about');

  const filteredFAQs = FAQ_ITEMS.filter(
    (item) => item.category === selectedCategory
  );

  const toggleExpanded = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <>
      <Seo
        title="FAQ – UK Vineyard Guide"
        description="Answers to common questions about the UK Vineyard Guide app — how it works, which regions are covered, how to get listed, and more."
        canonicalUrl={FAQ_URL}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(FAQ_SCHEMA)}</script>
      </Helmet>

      <Box
        component="main"
        sx={{
          px: { xs: '24px', md: '64px' },
          py: { xs: '48px', md: '64px' },
          backgroundColor: 'white',
        }}
      >
        <Box
          sx={{
            // maxWidth: '1200px',
            mx: 'auto',
            width: '100%',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '600px 1fr' },
            gap: { xs: '48px', md: '64px' },
            alignItems: 'start',
          }}
        >
          {/* Sidebar */}
          <Box
            sx={{
              backgroundImage: `url(${backgroundImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'top',
              backgroundRepeat: 'no-repeat',
              borderRadius: '16px',
              p: { xs: '24px', md: '48px' },
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              height: '65vh',
            }}
          >
            <Typography
              component="h1"
              sx={{
                ...typography.display,
                color: 'white',
                mb: { xs: '16px', md: '16px' },
                fontSize: {
                  xs: 'var(--font-size-36, 36px)',
                  sm: 'var(--font-size-48, 48px)',
                  md: typography.display.fontSize,
                },
              }}
            >
              FAQs
            </Typography>
            <Typography
              sx={{
                color: 'white',
                mb: '16px',
                display: 'block',
                fontSize: {
                  xs: 'var(--font-size-16, 16px)',
                  sm: 'var(--font-size-18, 18px)',
                  md: typography.heading5.fontSize,
                },
                fontFamily: typography.heading5.fontFamily,
                fontWeight: 325,
                width: '75%',
              }}
            >
              Whether you're planning your next tasting tour or looking to get
              your vineyard on the map, we’ve got the answers you need to get
              started
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '12px',
                flexWrap: 'wrap',
              }}
            >
              {CATEGORIES.map((category) => (
                <Button
                  key={category.id}
                  onClick={() => {
                    setSelectedCategory(category.id);
                    setExpandedIndex(null);
                    gtag('event', 'faq_category_click', {
                      event_category: 'FAQ',
                      event_label: category.label,
                    });
                  }}
                  sx={{
                    ...typography.bodySmall,
                    px: '20px',
                    py: '12px',
                    borderRadius: '24px',
                    border: `1px solid ${
                      selectedCategory === category.id
                        ? color.olive['300']
                        : color.charcoal['100']
                    }`,
                    backgroundColor:
                      selectedCategory === category.id
                        ? color.olive['300']
                        : 'transparent',
                    color:
                      selectedCategory === category.id
                        ? color.charcoal.default
                        : 'white',
                    fontWeight: 325,
                    textTransform: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: color.olive['300'],
                      color: color.charcoal.default,
                      backgroundColor:
                        selectedCategory === category.id
                          ? color.olive['300']
                          : color.cream['200'],
                    },
                  }}
                >
                  {category.label}
                </Button>
              ))}
            </Box>
          </Box>

          {/* Content */}
          <Box>
            <Box
              component="dl"
              sx={{
                m: 0,
                p: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                mb: '64px',
                mt: { xs: '32px', md: '48px' },
              }}
            >
              {filteredFAQs.map((item, index) => (
                <Box
                  key={item.question}
                  sx={{
                    borderBottom: `1px solid ${color.grey.default}`,
                    pb: '24px',
                    '&:last-child': {
                      borderBottom: 'none',
                      pb: 0,
                    },
                  }}
                >
                  <ButtonBase
                    component="dt"
                    onClick={() => toggleExpanded(index)}
                    aria-expanded={expandedIndex === index}
                    aria-controls={`faq-answer-${index}`}
                    id={`faq-question-${index}`}
                    sx={{
                      display: 'flex',
                      width: '100%',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: '16px',
                      textAlign: 'left',
                      userSelect: 'none',
                      '&:hover': {
                        opacity: 0.8,
                      },
                      '&:focus-visible': {
                        outline: `2px solid ${color.olive['300']}`,
                        outlineOffset: '2px',
                        borderRadius: '4px',
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        ...typography.heading5,
                        color: color.charcoal.default,
                        fontSize: {
                          xs: 'var(--font-size-16, 16px)',
                          md: typography.heading5.fontSize,
                        },
                        fontWeight: 350,
                        margin: 0,
                        flex: 1,
                      }}
                    >
                      {item.question}
                    </Typography>
                    <Box
                      aria-hidden="true"
                      sx={{
                        fontSize: '24px',
                        color: color.charcoal.default,
                        fontWeight: 'bold',
                        flexShrink: 0,
                        minWidth: '24px',
                        textAlign: 'center',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {expandedIndex === index ? '−' : '+'}
                    </Box>
                  </ButtonBase>

                  {expandedIndex === index && (
                    <Typography
                      component="dd"
                      id={`faq-answer-${index}`}
                      aria-labelledby={`faq-question-${index}`}
                      sx={{
                        ...typography.bodyText,
                        color: color.charcoal['400'],
                        m: 0,
                        lineHeight: 1.65,
                        mt: '16px',
                        fontSize: {
                          xs: 'var(--font-size-16, 16px)',
                          md: typography.bodyText.fontSize,
                        },
                      }}
                    >
                      {item.answer}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}
